import React, { useState, useEffect, useCallback } from 'react';
import { Database, Users, BarChart3, Target } from 'lucide-react';
import { DatasetInfo as DatasetInfoType } from '../types/api';
import { apiService } from '../services/api';
import { cn, formatNumber } from '../lib/utils';

interface DatasetInfoProps {
  targetVariable?: string;
}

const DatasetInfo: React.FC<DatasetInfoProps> = ({ targetVariable }) => {
  const [datasetInfo, setDatasetInfo] = useState<DatasetInfoType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDatasetInfo = useCallback(async () => {
    try {
      setLoading(true);
      const info = await apiService.getDatasetInfo(targetVariable);
      setDatasetInfo(info);
      setError(null);
    } catch (err) {
      setError('Error loading dataset information');
      console.error('Error loading dataset info:', err);
    } finally {
      setLoading(false);
    }
  }, [targetVariable]);

  useEffect(() => {
    loadDatasetInfo();
  }, [loadDatasetInfo]);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="animate-pulse">
          <div className="flex items-center space-x-2 mb-6">
            <div className="h-5 w-5 bg-muted rounded"></div>
            <div className="h-6 bg-muted rounded w-48"></div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="text-center text-destructive">
          <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm">{error}</p>
          <button
            onClick={loadDatasetInfo}
            className="mt-3 text-xs text-primary hover:text-primary/80 underline underline-offset-4"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  if (!datasetInfo) {
    return null;
  }

  const targetDistribution = datasetInfo.target_variable_info.distribution;
  const totalTarget = Object.values(targetDistribution).reduce((a, b) => a + b, 0);

  return (
    <div className="rounded-lg border bg-card p-6 space-y-6">
      <div className="flex items-center space-x-2">
        <Database className="h-5 w-5 text-foreground" />
        <h2 className="text-lg font-semibold tracking-tight">Dataset Overview</h2>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">Total Respondents</h3>
          </div>
          <div className="text-2xl font-bold">
            {formatNumber(datasetInfo.total_respondents)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Italian youth aged 18-34</div>
        </div>

        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="flex items-center space-x-2 mb-3">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">Available Variables</h3>
          </div>
          <div className="text-2xl font-bold">
            {formatNumber(datasetInfo.total_variables)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Clean variables for analysis</div>
        </div>

        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Target className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">Target Variable</h3>
          </div>
          <div className="text-sm font-medium mb-1">
            {datasetInfo.target_variable_info.name}
          </div>
          <div className="text-xs text-muted-foreground">
            {datasetInfo.target_variable_info.label}
          </div>
        </div>
      </div>

      {/* Target Variable Distribution */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">
          Target Distribution
        </h3>
        <div className="space-y-3">
          {Object.entries(targetDistribution).map(([key, value]) => {
            const percentage = (value / totalTarget) * 100;
            return (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium capitalize">
                    {key === 'si' ? 'Trust' : key === 'no' ? 'Distrust' : key}
                  </span>
                  <span className="text-muted-foreground">
                    {formatNumber(value)} ({percentage.toFixed(1)}%)
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn(
                      "h-full transition-all duration-500 rounded-full",
                      key === 'si' ? 'bg-primary' : 'bg-primary/60'
                    )}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg bg-muted/30 p-4">
        <h4 className="text-sm font-medium mb-3">Dataset Notes</h4>
        <ul className="text-xs text-muted-foreground space-y-1.5">
          <li>• Dataset: Youth and Europe Survey 2024 (Istituto Toniolo)</li>
          <li>• Population: Italian youth aged 18-34 years</li>
          <li>• Data leakage variables removed to prevent overfitting</li>
          <li>• Neutral responses (5/10) excluded to force binary classification</li>
        </ul>
      </div>
    </div>
  );
};

export default DatasetInfo;
